import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { authService } from "../services/authService";
import Logo from "../Image/Logo.jpg";

interface ResetPasswordError {
  message: string;
  field?: string;
}

interface PasswordValidation {
  minLength: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ResetPasswordError | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validation, setValidation] = useState<PasswordValidation>({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecial: false,
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      navigate("/forgot-password");
    }
  }, [token, navigate]);

  useEffect(() => {
    setValidation({
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  }, [password]);

  const isPasswordValid = Object.values(validation).every(Boolean);
  const passwordsMatch = password === confirmPassword && password.length > 0;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      setError({ message: "Please ensure your password meets all security requirements." });
      return;
    }

    if (!passwordsMatch) {
      setError({ message: "Passwords do not match." });
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      if (!token) {
        throw new Error('Reset token is required');
      }
      await authService.resetPassword(token, password);
      setIsSuccess(true);
    } catch (err: any) {
      console.error('Reset password error:', err);
      
      // The axios interceptor returns error object directly with message property
      // Handle validation errors with errors array
      let errorMessage = 'Failed to reset password. Please try again.';
      if (err?.errors && Array.isArray(err.errors) && err.errors.length > 0) {
        errorMessage = err.errors.join(', ');
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError({
        message: errorMessage,
        field: err?.statusCode === 400 ? 'password' : undefined
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Success Screen
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
            <div className="flex flex-col lg:flex-row">
              {/* Left Side - Success Branding */}
              <div className="lg:w-1/2 bg-gradient-to-br from-brand-600 via-brand-700 to-brand-800 relative overflow-hidden">
                <div className="h-full flex flex-col justify-center p-8 xl:p-12 relative min-h-[600px]">
                  {/* Animated Background Elements */}
                  <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-2xl transform rotate-12 animate-pulse"></div>
                    <div className="absolute bottom-20 right-16 w-40 h-40 bg-white/5 rounded-full animate-pulse" style={{ animationDelay: '3s' }}></div>
                    <div className="absolute top-1/2 left-8 w-24 h-24 bg-white/10 rounded-xl transform -rotate-12 animate-pulse" style={{ animationDelay: '6s' }}></div>
                  </div>
                  
                  {/* Content */}
                  <div className="relative z-10 text-white max-w-lg">
                    {/* Logo */}
                    <div className="mb-8">
                      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 inline-block border border-white/20">
                        <img 
                          src={Logo} 
                          alt="Company Logo" 
                          className="h-16 w-auto object-contain"
                        />
                      </div>
                    </div>

                    {/* Success Icon */}
                    <div className="mb-8">
                      <div className="w-24 h-24 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                        <i className="fas fa-check text-3xl"></i>
                      </div>
                    </div>

                    {/* Main Heading */}
                    <h1 className="text-3xl xl:text-4xl font-bold mb-6 leading-tight">
                      Password<br />
                      <span className="bg-gradient-to-r from-brand-200 to-brand-200 bg-clip-text text-transparent">Successfully Reset!</span>
                    </h1>
                    
                    <p className="text-lg xl:text-xl text-white/90 mb-8 leading-relaxed">
                      Great! Your password has been updated successfully. You can now login with your new password and access your account securely.
                    </p>

                    {/* Security Features */}
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4 group">
                        <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border border-white/20">
                          <i className="fas fa-shield-alt text-lg"></i>
                        </div>
                        <div>
                          <h3 className="font-semibold">Password Secured</h3>
                          <p className="text-white/80 text-sm">Your new password is encrypted and protected</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 group">
                        <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border border-white/20">
                          <i className="fas fa-lock text-lg"></i>
                        </div>
                        <div>
                          <h3 className="font-semibold">Account Security Updated</h3>
                          <p className="text-white/80 text-sm">All security measures have been refreshed</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 group">
                        <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border border-white/20">
                          <i className="fas fa-user-check text-lg"></i>
                        </div>
                        <div>
                          <h3 className="font-semibold">Ready to Access</h3>
                          <p className="text-white/80 text-sm">You can now login with your new credentials</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Success Actions */}
              <div className="lg:w-1/2 flex items-center justify-center p-6 xl:p-8">
                <div className="w-full max-w-md">
                  {/* Mobile Logo */}
                  <div className="lg:hidden text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-600 to-brand-600 p-3 mb-4">
                      <img 
                        src={Logo} 
                        alt="Company Logo" 
                        className="h-8 w-auto object-contain"
                      />
                    </div>
                  </div>

                  {/* Success Message */}
                  <div className="mb-8">
                    <div className="flex items-center mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-l-4 border-green-500">
                      <div className="p-2 bg-green-100 rounded-lg mr-3">
                        <i className="fas fa-check-circle text-2xl text-green-600"></i>
                      </div>
                      <div className="text-left">
                        <h2 className="text-xl font-bold text-gray-900">Success!</h2>
                        <p className="text-sm text-gray-600">Your password has been reset successfully</p>
                      </div>
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="bg-brand-50 rounded-xl p-6 mb-8 border border-brand-200">
                    <h3 className="font-semibold text-brand-900 mb-3 flex items-center">
                      <i className="fas fa-info-circle mr-2"></i>
                      What's Next:
                    </h3>
                    <ul className="space-y-3 text-sm text-brand-800">
                      <li className="flex items-start">
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-brand-100 rounded-full text-xs font-bold text-brand-600 mr-3 flex-shrink-0 mt-0.5">1</span>
                        Click "Continue to Login" below
                      </li>
                      <li className="flex items-start">
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-brand-100 rounded-full text-xs font-bold text-brand-600 mr-3 flex-shrink-0 mt-0.5">2</span>
                        Enter your email and new password
                      </li>
                      <li className="flex items-start">
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-brand-100 rounded-full text-xs font-bold text-brand-600 mr-3 flex-shrink-0 mt-0.5">3</span>
                        Access your secure account
                      </li>
                    </ul>
                  </div>

                  {/* Actions */}
                  <div className="space-y-4">
                    <button
                      onClick={() => navigate('/login')}
                      className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-brand-600 to-brand-600 hover:from-brand-700 hover:to-brand-700 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <i className="fas fa-sign-in-alt mr-2"></i>
                      Continue to Login
                    </button>

                    {/* Help Text */}
                    <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
                      <div className="flex items-start">
                        <i className="fas fa-lightbulb text-brand-500 mr-2 mt-1"></i>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">Security Tip</h4>
                          <p className="text-xs text-gray-600 mt-1">
                            Make sure to keep your new password secure and don't share it with anyone.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Main Reset Password Form
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
          <div className="flex flex-col lg:flex-row">
            {/* Left Side - Branding Panel */}
            <div className="lg:w-1/2 bg-gradient-to-br from-brand-600 via-brand-700 to-brand-800 relative overflow-hidden rounded-r-3xl">
              <div className="h-full flex flex-col justify-center p-8 xl:p-12 relative min-h-[600px]">
                {/* Animated Background Elements */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-2xl transform rotate-12 animate-pulse"></div>
                  <div className="absolute bottom-20 right-16 w-40 h-40 bg-white/5 rounded-full animate-pulse" style={{ animationDelay: '3s' }}></div>
                  <div className="absolute top-1/2 left-8 w-24 h-24 bg-white/10 rounded-xl transform -rotate-12 animate-pulse" style={{ animationDelay: '6s' }}></div>
                </div>
                
                {/* Content */}
                <div className="relative z-10 text-white max-w-lg">
                  {/* Logo */}
                  <div className="mb-8">
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 inline-block border border-white/20">
                      <img 
                        src={Logo} 
                        alt="Company Logo" 
                        className="h-16 w-auto object-contain"
                      />
                    </div>
                  </div>

                  {/* Main Heading */}
                  <h1 className="text-3xl xl:text-4xl font-bold mb-6 leading-tight">
                    Create Your<br />
                    <span className="bg-gradient-to-r from-brand-200 to-brand-200 bg-clip-text text-transparent">New Password</span>
                  </h1>
                  
                  <p className="text-lg xl:text-xl text-white/90 mb-8 leading-relaxed">
                    Choose a strong, secure password that you haven't used before. Your new password will keep your account protected.
                  </p>

                  {/* Security Features */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4 group">
                      <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border border-white/20">
                        <i className="fas fa-shield-alt text-lg"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold">Advanced Encryption</h3>
                        <p className="text-white/80 text-sm">Your password is secured with industry-standard encryption</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 group">
                      <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border border-white/20">
                        <i className="fas fa-user-shield text-lg"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold">Account Protection</h3>
                        <p className="text-white/80 text-sm">Multi-layered security keeps your data safe</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 group">
                      <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border border-white/20">
                        <i className="fas fa-key text-lg"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold">Secure Reset Process</h3>
                        <p className="text-white/80 text-sm">Verified token-based authentication system</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Reset Form */}
            <div className="lg:w-1/2 flex items-center justify-center p-6 xl:p-8">
              <div className="w-full max-w-md">
                {/* Mobile Logo */}
                <div className="lg:hidden text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-600 to-brand-600 p-3 mb-4">
                    <img 
                      src={Logo} 
                      alt="Company Logo" 
                      className="h-8 w-auto object-contain"
                    />
                  </div>
                </div>

                {/* Header Section */}
                <div className="mb-8">
                  <div className="flex items-center mb-6 p-4 bg-gradient-to-r from-brand-50 to-brand-50 rounded-xl border-l-4 border-brand-500">
                    <div className="p-2 bg-brand-100 rounded-lg mr-3">
                      <i className="fas fa-key text-xl text-brand-600"></i>
                    </div>
                    <div className="text-left">
                      <h2 className="text-xl font-bold text-gray-900">Set New Password</h2>
                      <p className="text-sm text-gray-600">Create a secure password for your account</p>
                    </div>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                    <div className="flex items-center">
                      <i className="fas fa-exclamation-triangle text-red-600 mr-2"></i>
                      <p className="text-red-800 font-medium">{error.message}</p>
                    </div>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* New Password Field */}
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      New Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        required
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 hover:border-gray-300 transition-all duration-200 ${
                          error?.field === 'password' 
                            ? 'border-red-300 bg-red-50' 
                            : 'border-gray-200 bg-white'
                        }`}
                        placeholder="Enter your new password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (error) setError(null);
                        }}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-lg`}></i>
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password Field */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm New Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        required
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 hover:border-gray-300 transition-all duration-200 ${
                          confirmPassword && !passwordsMatch
                            ? 'border-red-300 bg-red-50' 
                            : 'border-gray-200 bg-white'
                        }`}
                        placeholder="Confirm your new password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (error) setError(null);
                        }}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} text-lg`}></i>
                      </button>
                    </div>
                    {confirmPassword && !passwordsMatch && (
                      <p className="mt-2 text-sm text-red-600">
                        <i className="fas fa-times-circle mr-1"></i>
                        Passwords do not match
                      </p>
                    )}
                  </div>

                  {/* Password Requirements */}
                  {password && (
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                        <i className="fas fa-check-circle mr-2 text-brand-500"></i>
                        Password Requirements
                      </h4>
                      <div className="grid grid-cols-1 gap-2 text-sm">
                        {[
                          { key: 'minLength', label: 'At least 8 characters', icon: 'fa-list-ol' },
                          { key: 'hasUpperCase', label: 'One uppercase letter', icon: 'fa-font' },
                          { key: 'hasLowerCase', label: 'One lowercase letter', icon: 'fa-font-case' },
                          { key: 'hasNumber', label: 'One number', icon: 'fa-hashtag' },
                          { key: 'hasSpecial', label: 'One special character', icon: 'fa-at' }
                        ].map(({ key, label, icon }) => (
                          <div
                            key={key}
                            className={`flex items-center transition-colors ${
                              validation[key as keyof PasswordValidation] 
                                ? 'text-green-600' 
                                : 'text-gray-400'
                            }`}
                          >
                            <i className={`fas ${validation[key as keyof PasswordValidation] ? 'fa-check' : 'fa-circle'} mr-2 text-xs`}></i>
                            <span>{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || !isPasswordValid || !passwordsMatch}
                    className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg text-sm font-semibold text-white transition-all duration-200 shadow-md hover:shadow-lg ${
                      isLoading || !isPasswordValid || !passwordsMatch
                        ? 'bg-gray-400 cursor-not-allowed opacity-50'
                        : 'bg-gradient-to-r from-brand-600 to-brand-600 hover:from-brand-700 hover:to-brand-700'
                    }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <i className="fas fa-spinner fa-spin mr-3"></i>
                        Updating Password...
                      </div>
                    ) : (
                      <>
                        <i className="fas fa-lock mr-2"></i>
                        Reset Password
                      </>
                    )}
                  </button>
                </form>

                {/* Help Section */}
                <div className="mt-8">
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white font-medium text-gray-500">
                        Need help?
                      </span>
                    </div>
                  </div>

                  <Link
                    to="/login"
                    className="w-full flex justify-center items-center py-3 px-4 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium group"
                  >
                    <i className="fas fa-arrow-left mr-2 group-hover:text-brand-500 transition-colors"></i>
                    Back to Login
                  </Link>

                  {/* Info Box */}
                  <div className="mt-6 p-4 rounded-xl bg-gray-50 border border-gray-200">
                    <div className="flex items-start">
                      <i className="fas fa-info-circle text-brand-500 mr-2 mt-1"></i>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Security Note</h4>
                        <p className="text-xs text-gray-600 mt-1">
                          Choose a password you haven't used before and keep it secure. Never share your password with anyone.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}